import { SHARPE_MIN_RETURNS, type MasterEquityPoint } from '@app/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  clearDisplayStats,
  createMaster,
  getMasterEquity,
  getMasters,
  getRegistryMarkets,
  patchMaster,
  putDisplayStats,
  putStrategy,
  recomputeMasterEquity,
  setStrategyStatus,
  type AdminMaster,
  type DisplayStatsInput,
  type StrategyInput,
} from '../api/admin'
import { Button, Chip, Empty, Field, Input, Modal, Panel, Select, Spacer, StatusPill, Switch, Table, Tabs, Textarea, Toolbar, describeError, money, tableClass, ui, useToast, useTotp, when } from '../components/ui/Ui'

type EditorTab = 'profile' | 'strategy' | 'stats' | 'equity'

const DEFAULT_STRATEGY: StrategyInput = {
  marketIds: [],
  marketType: 'spot',
  sideBias: 'long_only',
  leverage: 1,
  issueSignals: false,
  signalAccessKind: 'base',
  signalWindowSec: 1500,
  positionFraction: '0.08',
  maxOpenPositions: 2,
  minIntervalSec: 300,
  maxIntervalSec: 1800,
  takeProfitPct: '1.5',
  stopLossPct: '1',
  maxHoldSec: 21_600,
  randomSeed: String(Date.now() % 1_000_000_000),
  targetWinRate: '0.6',
}

const pct = (value: string | null | undefined): string => (value === null || value === undefined ? '—' : `${money(value, 2)} %`)

/**
 * The Sharpe ratio is always the computed one — there is no showcase value for
 * it — so the table reads it from `computedStats`. Without a figure it says why.
 */
function SharpeCell({ stats }: { stats: AdminMaster['computedStats'] }): JSX.Element {
  if (stats?.sharpe !== null && stats?.sharpe !== undefined) return <>{signedRatio(stats.sharpe)}</>
  const trading = stats?.sharpeTradingDays ?? 0
  return trading < SHARPE_MIN_RETURNS ? (
    <span
      className={tableClass.muted}
      title={`Нужно не меньше ${SHARPE_MIN_RETURNS} дней в рынке — завершённых дней, когда у мастера была открытая позиция. Дни без позиций в счёт не идут.`}
    >
      {trading} из {SHARPE_MIN_RETURNS} дн. в рынке
    </span>
  ) : (
    <span className={tableClass.muted} title="Кривая эквити не менялась — отношение не определено">
      нет колебаний
    </span>
  )
}

const ROI_LABELS = { roi7d: 'ROI 7 дней', roi30d: 'ROI 30 дней', roiAll: 'ROI за всё время' } as const
type RoiKey = keyof typeof ROI_LABELS

/**
 * Showcase returns the operator entered whose sign disagrees with the honest
 * Sharpe ratio. The card shows both next to each other, and the ratio cannot
 * be set by hand, so the operator should see the contradiction before a user
 * does. `entered` holds only the fields the operator actually overrides.
 */
function sharpeConflicts(entered: Partial<Record<RoiKey, string | null>>, sharpe: string | null | undefined): Array<{ key: RoiKey; value: string }> {
  if (sharpe === null || sharpe === undefined) return []
  const ratio = Number(sharpe)
  if (!Number.isFinite(ratio) || ratio === 0) return []
  return (Object.keys(ROI_LABELS) as RoiKey[]).flatMap((key) => {
    const value = entered[key]
    if (value === null || value === undefined || value === '') return []
    const roi = Number(value)
    return Number.isFinite(roi) && roi !== 0 && Math.sign(roi) !== Math.sign(ratio) ? [{ key, value }] : []
  })
}

/**
 * The operator's overrides: shown figures that differ from the computed ones.
 * A figure equal to the computed one is the fact, whoever typed it — honest
 * figures disagreeing with each other are data, not a contradiction.
 */
function overriddenRoi(
  shown: Partial<Record<RoiKey, string | null | undefined>> | null | undefined,
  computed: AdminMaster['computedStats'],
): Partial<Record<RoiKey, string>> {
  const out: Partial<Record<RoiKey, string>> = {}
  for (const key of Object.keys(ROI_LABELS) as RoiKey[]) {
    const value = shown?.[key]
    if (value === null || value === undefined || value === '') continue
    if (Number(value) !== Number(computed?.[key] ?? 'NaN')) out[key] = value
  }
  return out
}

function signedPct(value: string): string {
  const n = Number(value)
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${money(String(Math.abs(n)), 2)} %`
}

function signedRatio(value: string): string {
  const n = Number(value)
  return `${n < 0 ? '−' : ''}${money(String(Math.abs(n)), 2)}`
}

function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('ru-RU', { timeZone: 'UTC' })
}

/** The curve lives in UTC days, so its times are shown in UTC too. */
function utcTime(iso: string): string {
  return `${new Date(iso).toISOString().slice(11, 16)} UTC`
}

/**
 * The masters are the product: this is where an operator creates one, runs
 * its strategy and sets the figures its card shows. The computed figures sit
 * next to the shown ones on purpose, so the difference is never hidden.
 */
export default function Masters(): JSX.Element {
  const [editing, setEditing] = useState<AdminMaster | null | 'new'>(null)
  const queryClient = useQueryClient()
  const toast = useToast()

  const masters = useQuery({ queryKey: ['masters'], queryFn: getMasters, refetchInterval: 20_000 })
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['masters'] })
    void queryClient.invalidateQueries({ queryKey: ['overview'] })
  }

  const toggle = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'running' | 'paused' | 'stopped' }) => setStrategyStatus(id, status),
    onSuccess: () => {
      toast('Стратегия обновлена')
      refresh()
    },
    onError: (error) => toast(describeError(error), true),
  })

  const rows = masters.data ?? []

  return (
    <>
      <Toolbar>
        <span className={tableClass.muted}>Мастера — алгоритмические стратегии. Показанные цифры задаёт оператор, честные считает платформа.</span>
        <Spacer />
        <Button variant="primary" onClick={() => setEditing('new')}>
          Новый мастер
        </Button>
      </Toolbar>
      <Panel>
        {masters.isError ? (
          <Empty>{describeError(masters.error)}</Empty>
        ) : rows.length === 0 ? (
          <Empty>{masters.isPending ? 'Загрузка…' : 'Мастеров пока нет'}</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Мастер</th>
                <th>Профиль</th>
                <th>Стратегия</th>
                <th className={tableClass.right}>Подписчики</th>
                <th className={tableClass.right}>Капитал</th>
                <th className={tableClass.right} title="Что видит пользователь">ROI 30д</th>
                <th className={tableClass.right} title="Что посчитала платформа по сделкам">факт</th>
                <th className={tableClass.right} title="Коэффициент Шарпа по дневной кривой эквити — всегда факт, вручную не задаётся">Шарп</th>
                <th className={tableClass.right}>Сделок</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.userId}>
                  <td>
                    <strong>{row.displayName}</strong>
                    <div className={tableClass.muted}>{row.isPublic ? 'в рейтинге' : 'скрыт'} · мин. {money(row.minCopyAmount, 0)} USDT</div>
                  </td>
                  <td>
                    <StatusPill status={row.status} />
                  </td>
                  <td>
                    <StatusPill status={row.strategy?.status ?? 'stopped'} />
                    <div className={tableClass.muted} title="Последнее действие стратегии">{row.strategy?.lastError ?? (row.strategy?.lastActionAt ? when(row.strategy.lastActionAt) : 'действий ещё не было')}</div>
                  </td>
                  <td className={`${tableClass.right} num`}>{row.stats?.followersCount ?? 0}</td>
                  <td className={`${tableClass.right} num`}>{money(row.stats?.aum, 0)}</td>
                  <td className={`${tableClass.right} num`}>{pct(row.stats?.roi30d)}</td>
                  <td className={`${tableClass.right} num ${tableClass.muted}`}>{pct(row.computedStats?.roi30d)}</td>
                  <td className={`${tableClass.right} num`}>
                    <SharpeCell stats={row.computedStats} />
                    {sharpeConflicts(overriddenRoi(row.stats, row.computedStats), row.computedStats?.sharpe).length > 0 ? (
                      <div className={ui.error} title="Знак витринного ROI не совпадает со знаком коэффициента Шарпа по реальным сделкам. Подробности — во вкладке «Цифры на карточке».">
                        расходится с витриной
                      </div>
                    ) : null}
                  </td>
                  <td className={`${tableClass.right} num`}>{row.computedStats?.totalTrades ?? 0}</td>
                  <td className={tableClass.actions}>
                    {row.strategy?.status === 'running' ? (
                      <Button size="sm" onClick={() => toggle.mutate({ id: row.userId, status: 'paused' })}>
                        Пауза
                      </Button>
                    ) : (
                      <Button size="sm" variant="primary" onClick={() => toggle.mutate({ id: row.userId, status: 'running' })}>
                        Запустить
                      </Button>
                    )}
                    <Button size="sm" onClick={() => setEditing(row)}>
                      Открыть
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      {editing !== null ? <MasterEditor master={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={refresh} /> : null}
    </>
  )
}

function MasterEditor({ master, onClose, onSaved }: { master: AdminMaster | null; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [tab, setTab] = useState<EditorTab>(master === null ? 'profile' : 'profile')
  const [email, setEmail] = useState(master?.email ?? '')
  const [displayName, setDisplayName] = useState(master?.displayName ?? '')
  const [bio, setBio] = useState(master?.bio ?? '')
  const [tags, setTags] = useState((master?.tags ?? []).join(', '))
  const [minCopyAmount, setMinCopyAmount] = useState(master?.minCopyAmount ?? '50')
  const [commissionRate, setCommissionRate] = useState(master?.commissionRate ?? '0')
  const [isPublic, setIsPublic] = useState(master?.isPublic ?? true)
  const [status, setStatus] = useState<AdminMaster['status']>(master?.status ?? 'active')
  const [strategy, setStrategy] = useState<StrategyInput>(master?.strategy ? { ...master.strategy } : DEFAULT_STRATEGY)
  const [strategyStatus, setStrategyStatus] = useState<'stopped' | 'running' | 'paused'>(
    master?.strategy?.status === 'running' || master?.strategy?.status === 'paused' ? master.strategy.status : 'stopped',
  )
  const [shown, setShown] = useState<DisplayStatsInput>({
    roi7d: master?.stats?.roi7d ?? null,
    roi30d: master?.stats?.roi30d ?? null,
    roiAll: master?.stats?.roiAll ?? null,
    winRate: master?.stats?.winRate ?? null,
    maxDrawdown: master?.stats?.maxDrawdown ?? null,
    riskLevel: master?.stats?.riskLevel ?? null,
    totalTrades: master?.stats?.totalTrades ?? null,
    followersCount: null,
    aum: null,
  })
  const toast = useToast()
  const withTotp = useTotp()
  const markets = useQuery({ queryKey: ['registry-markets'], queryFn: getRegistryMarkets, staleTime: 60_000 })

  useEffect(() => {
    setTab('profile')
  }, [master?.userId])

  const tradableMarkets = (markets.data ?? []).filter((market) => market.marketType === strategy.marketType && market.isEnabled)
  const tagList = tags.split(',').map((tag) => tag.trim()).filter(Boolean)

  const save = useMutation({
    mutationFn: async () => {
      if (master === null) {
        return withTotp((code) =>
          createMaster(
            { email, displayName, bio: bio || null, tags: tagList, minCopyAmount, commissionRate, strategy, strategyStatus: strategyStatus === 'running' ? 'running' : 'stopped' },
            code,
          ),
        )
      }
      await patchMaster(master.userId, { displayName, bio: bio || null, tags: tagList, minCopyAmount, commissionRate, isPublic, status })
      await putStrategy(master.userId, strategy, strategyStatus)
      return master
    },
    onSuccess: (result) => {
      if (result === null) return
      toast(master === null ? 'Мастер создан' : 'Сохранено')
      onSaved()
      onClose()
    },
    onError: (error) => toast(describeError(error), true),
  })

  const saveStats = useMutation({
    mutationFn: () => {
      if (master === null) throw new Error('save the master first')
      const cleaned = Object.fromEntries(Object.entries(shown).filter(([, value]) => value !== null && value !== '')) as DisplayStatsInput
      return withTotp((code) => putDisplayStats(master.userId, cleaned, code))
    },
    onSuccess: (result) => {
      if (result === null) return
      toast('Показываемые цифры сохранены и записаны в аудит')
      onSaved()
    },
    onError: (error) => toast(describeError(error), true),
  })

  const resetStats = useMutation({
    mutationFn: () => {
      if (master === null) throw new Error('nothing to reset')
      return withTotp((code) => clearDisplayStats(master.userId, code))
    },
    onSuccess: (result) => {
      if (result === null) return
      toast('Карточка показывает вычисленные цифры')
      onSaved()
      onClose()
    },
    onError: (error) => toast(describeError(error), true),
  })

  const queryClient = useQueryClient()
  const recompute = useMutation({
    mutationFn: () => {
      if (master === null) throw new Error('save the master first')
      return recomputeMasterEquity(master.userId)
    },
    onSuccess: (result) => {
      queryClient.setQueryData(['master-equity', result.curve.masterId], result.curve)
      const figure = result.curve.sharpe === null ? 'пока нет' : signedRatio(result.curve.sharpe)
      toast(`Пересчитано по сделкам: восстановлено дней — ${result.backfilled}, обновлено — ${result.refreshed}. Шарп: ${figure}`)
      onSaved()
    },
    onError: (error) => toast(describeError(error), true),
  })

  // What the operator is about to show against the honest ratio: only the
  // fields that differ from the computed figures — an empty field, or one
  // equal to the fact, cannot contradict anything.
  const conflicts = sharpeConflicts(overriddenRoi(shown, master?.computedStats ?? null), master?.computedStats?.sharpe)

  const set = <K extends keyof StrategyInput>(key: K, value: StrategyInput[K]): void => setStrategy((current) => ({ ...current, [key]: value }))
  const setStat = <K extends keyof DisplayStatsInput>(key: K, value: DisplayStatsInput[K]): void => setShown((current) => ({ ...current, [key]: value }))
  const toggleMarket = (id: number): void =>
    set('marketIds', strategy.marketIds.includes(id) ? strategy.marketIds.filter((item) => item !== id) : [...strategy.marketIds, id])

  return (
    <Modal
      open
      onClose={onClose}
      title={master === null ? 'Новый мастер' : master.displayName ?? 'Мастер'}
      subtitle={master === null ? 'Пользователь, профиль, стратегия и виртуальный капитал 10 000 USDT создаются одной транзакцией.' : master.email}
      wide
      footer={
        tab === 'equity' ? (
          <>
            <Spacer />
            <Button variant="quiet" onClick={onClose}>
              Закрыть
            </Button>
            <Button onClick={() => recompute.mutate()} loading={recompute.isPending}>
              Пересчитать по сделкам
            </Button>
          </>
        ) : tab === 'stats' ? (
          <>
            {master ? (
              <Button variant="danger" onClick={() => resetStats.mutate()} loading={resetStats.isPending}>
                Показывать факт
              </Button>
            ) : null}
            <Spacer />
            <Button variant="quiet" onClick={onClose}>
              Закрыть
            </Button>
            <Button variant="primary" disabled={master === null} onClick={() => saveStats.mutate()} loading={saveStats.isPending}>
              Сохранить цифры
            </Button>
          </>
        ) : (
          <>
            <Button variant="quiet" onClick={onClose}>
              Отмена
            </Button>
            <Button
              variant="primary"
              disabled={displayName.trim().length < 2 || strategy.marketIds.length === 0 || (master === null && !email.includes('@'))}
              onClick={() => save.mutate()}
              loading={save.isPending}
            >
              {master === null ? 'Создать' : 'Сохранить'}
            </Button>
          </>
        )
      }
    >
      <Tabs
        value={tab}
        options={[
          { value: 'profile', label: 'Профиль' },
          { value: 'strategy', label: 'Стратегия' },
          { value: 'stats', label: 'Цифры на карточке' },
          ...(master === null ? [] : [{ value: 'equity' as const, label: 'Кривая и Шарп' }]),
        ]}
        onChange={setTab}
      />

      {tab === 'profile' ? (
        <div className={ui.stack}>
          <div className={ui.grid2}>
            {master === null ? (
              <Field label="Служебная почта" hint="Ключ аккаунта мастера, входа по ней нет.">
                <Input value={email} onChange={(event) => setEmail(event.target.value)} placeholder="master.trend@19crt.internal" />
              </Field>
            ) : null}
            <Field label="Имя на карточке">
              <Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
            </Field>
            <Field label="Минимальная подписка, USDT">
              <Input mono value={minCopyAmount} onChange={(event) => setMinCopyAmount(event.target.value)} />
            </Field>
            <Field label="Комиссия с прибыли" hint="Доля реализованной прибыли подписчика (0.1 = 10 %), снимается раз в час по high-water mark и при остановке. 0 — без комиссии.">
              <Input mono value={commissionRate} onChange={(event) => setCommissionRate(event.target.value.replace(/[^0-9.]/g, ''))} />
            </Field>
            <Field label="Теги" hint="Через запятую, до восьми.">
              <Input value={tags} onChange={(event) => setTags(event.target.value)} />
            </Field>
            {master !== null ? (
              <>
                <Field label="Статус">
                  <Select value={status} onChange={(event) => setStatus(event.target.value as AdminMaster['status'])}>
                    <option value="active">Принимает подписчиков</option>
                    <option value="paused">На паузе</option>
                    <option value="blocked">Заблокирован</option>
                  </Select>
                </Field>
                <Field label="Видимость">
                  <Select value={isPublic ? 'public' : 'hidden'} onChange={(event) => setIsPublic(event.target.value === 'public')}>
                    <option value="public">В рейтинге</option>
                    <option value="hidden">Скрыт</option>
                  </Select>
                </Field>
              </>
            ) : null}
          </div>
          <Field label="Описание">
            <Textarea value={bio} onChange={(event) => setBio(event.target.value)} />
          </Field>
        </div>
      ) : null}

      {tab === 'strategy' ? (
        <div className={ui.stack}>
          <div className={ui.grid3}>
            <Field label="Тип рынка" hint="Спот — только покупки. Фьючерсы — лонг и шорт с плечом, изолированная маржа.">
              <Select
                value={strategy.marketType}
                onChange={(event) => {
                  const marketType = event.target.value as StrategyInput['marketType']
                  setStrategy((current) => ({
                    ...current,
                    marketType,
                    marketIds: [],
                    sideBias: marketType === 'futures' ? 'both' : 'long_only',
                    leverage: marketType === 'futures' ? Math.max(1, current.leverage) : 1,
                  }))
                }}
              >
                <option value="spot">Спот</option>
                <option value="futures">Фьючерсы</option>
              </Select>
            </Field>
            {strategy.marketType === 'futures' ? (
              <>
                <Field label="Плечо" hint="1–100; не выше максимума рынка. Подписчики копируют с тем же плечом.">
                  <Input mono type="number" min={1} max={100} value={strategy.leverage} onChange={(event) => set('leverage', Math.max(1, Math.min(100, Number(event.target.value) || 1)))} />
                </Field>
                <Field label="Направление">
                  <Select value={strategy.sideBias} onChange={(event) => set('sideBias', event.target.value as StrategyInput['sideBias'])}>
                    <option value="both">Лонг и шорт</option>
                    <option value="long_only">Только лонг</option>
                    <option value="short_only">Только шорт</option>
                  </Select>
                </Field>
              </>
            ) : null}
          </div>
          <Field label="Рынки" hint={strategy.marketType === 'futures' ? 'Бессрочные контракты, по которым стратегия открывает позиции.' : 'Спотовые пары, по которым стратегия открывает позиции.'}>
            <div className={ui.chips}>
              {tradableMarkets.map((market) => (
                <Chip key={market.id} pressed={strategy.marketIds.includes(market.id)} onClick={() => toggleMarket(market.id)}>
                  {market.symbol}
                </Chip>
              ))}
            </div>
          </Field>
          <div className={ui.grid3}>
            <Field label="Доля капитала на сделку" hint={strategy.marketType === 'futures' ? '0.08 = 8 % капитала как маржа; объём = маржа × плечо. Подписчик — та же доля своей суммы.' : '0.08 = 8 % капитала мастера; подписчик рискует той же долей своей суммы.'}>
              <Input mono value={strategy.positionFraction} onChange={(event) => set('positionFraction', event.target.value)} />
            </Field>
            <Field label="Макс. открытых позиций">
              <Input mono type="number" min={1} max={20} value={strategy.maxOpenPositions} onChange={(event) => set('maxOpenPositions', Number(event.target.value))} />
            </Field>
            <Field label="Целевой винрейт" hint="0–1; пусто — без подстройки.">
              <Input mono value={strategy.targetWinRate ?? ''} onChange={(event) => set('targetWinRate', event.target.value || null)} />
            </Field>
            <Field label="Интервал от, сек">
              <Input mono type="number" min={30} value={strategy.minIntervalSec} onChange={(event) => set('minIntervalSec', Number(event.target.value))} />
            </Field>
            <Field label="Интервал до, сек">
              <Input mono type="number" min={30} value={strategy.maxIntervalSec} onChange={(event) => set('maxIntervalSec', Number(event.target.value))} />
            </Field>
            <Field label="Макс. удержание, сек" hint="Пусто — без ограничения.">
              <Input mono type="number" value={strategy.maxHoldSec ?? ''} onChange={(event) => set('maxHoldSec', event.target.value === '' ? null : Number(event.target.value))} />
            </Field>
            <Field label="Тейк-профит, %">
              <Input mono value={strategy.takeProfitPct ?? ''} onChange={(event) => set('takeProfitPct', event.target.value || null)} />
            </Field>
            <Field label="Стоп-лосс, %">
              <Input mono value={strategy.stopLossPct ?? ''} onChange={(event) => set('stopLossPct', event.target.value || null)} />
            </Field>
            <Field label="Зерно случайности" hint="Одно зерно — одна воспроизводимая последовательность решений.">
              <Input mono value={strategy.randomSeed} onChange={(event) => set('randomSeed', event.target.value.replace(/\D/g, ''))} />
            </Field>
          </div>
          <div className={ui.grid3}>
            <Field label="Выпускать сигналы" hint="Каждый вход стратегии становится живым сигналом с кодом, целями стратегии и окном.">
              <Switch checked={strategy.issueSignals} label="Выпускать сигналы" onChange={(next) => set('issueSignals', next)} />
            </Field>
            {strategy.issueSignals ? (
              <>
                <Field label="Доступ к сигналам">
                  <Select value={strategy.signalAccessKind} onChange={(event) => set('signalAccessKind', event.target.value as StrategyInput['signalAccessKind'])}>
                    <option value="public">Всем</option>
                    <option value="base">Базовый</option>
                    <option value="premium">Премиум</option>
                    <option value="bonus">Бонусный</option>
                    <option value="agent">Агентский</option>
                  </Select>
                </Field>
                <Field label="Окно сигнала, мин" hint="Сколько минут код принимается после входа.">
                  <Input mono type="number" min={1} max={1440} value={Math.round(strategy.signalWindowSec / 60)} onChange={(event) => set('signalWindowSec', Math.max(1, Number(event.target.value) || 1) * 60)} />
                </Field>
              </>
            ) : null}
          </div>
          <Field label="Состояние стратегии">
            <Select value={strategyStatus} onChange={(event) => setStrategyStatus(event.target.value as 'stopped' | 'running' | 'paused')}>
              <option value="running">Работает</option>
              <option value="paused">На паузе</option>
              <option value="stopped">Остановлена</option>
            </Select>
          </Field>
        </div>
      ) : null}

      {tab === 'stats' ? (
        <div className={ui.stack}>
          <p className={ui.hint}>
            Слева — что увидит пользователь, справа — что посчитала платформа по реальным сделкам. Пустое поле показывает факт. Каждое сохранение пишется в аудит с обеими версиями.
          </p>
          <div className={ui.grid3}>
            {(
              [
                ['roi7d', 'ROI 7 дней, %'],
                ['roi30d', 'ROI 30 дней, %'],
                ['roiAll', 'ROI за всё время, %'],
                ['winRate', 'Винрейт, 0–1'],
                ['maxDrawdown', 'Просадка, %'],
                ['totalTrades', 'Сделок'],
              ] as const
            ).map(([key, label]) => (
              <Field key={key} label={label} hint={`Факт: ${master?.computedStats ? String(master.computedStats[key]) : '—'}`}>
                <Input
                  mono
                  value={shown[key] === null || shown[key] === undefined ? '' : String(shown[key])}
                  onChange={(event) => setStat(key, key === 'totalTrades' ? (event.target.value === '' ? null : Number(event.target.value)) : (event.target.value || null) as never)}
                />
              </Field>
            ))}
            <Field label="Риск" hint={`Факт: ${master?.computedStats?.riskLevel ?? '—'}`}>
              <Select value={shown.riskLevel ?? ''} onChange={(event) => setStat('riskLevel', (event.target.value || null) as DisplayStatsInput['riskLevel'])}>
                <option value="">Как посчитано</option>
                <option value="low">Низкий</option>
                <option value="medium">Средний</option>
                <option value="high">Высокий</option>
              </Select>
            </Field>
          </div>
          {conflicts.length > 0 && master?.computedStats?.sharpe ? (
            <p className={ui.error} role="status">
              Витрина расходится с фактом:{' '}
              {conflicts.map((item) => `${ROI_LABELS[item.key]} ${signedPct(item.value)}`).join(', ')}, а коэффициент Шарпа по реальным сделкам{' '}
              {signedRatio(master.computedStats.sharpe)}. В приложении оба числа стоят рядом, и Шарп вручную не меняется — пользователь увидит противоречие.
            </p>
          ) : null}
          <p className={ui.hint}>
            Коэффициент Шарпа вручную не задаётся: карточка всегда показывает факт по дневной кривой эквити (сейчас —{' '}
            <span className="num">
              <SharpeCell stats={master?.computedStats ?? null} />
            </span>
            ). Подробности — во вкладке «Кривая и Шарп».
          </p>
        </div>
      ) : null}

      {tab === 'equity' && master !== null ? <EquityCurvePanel masterId={master.userId} /> : null}
    </Modal>
  )
}

/**
 * The honest daily equity curve behind the Sharpe ratio, newest day first.
 * Read-only: the points are taken by the server's clock; the operator can only
 * have the past rebuilt from the books and the ratio restated.
 */
function EquityCurvePanel({ masterId }: { masterId: string }): JSX.Element {
  const equity = useQuery({ queryKey: ['master-equity', masterId], queryFn: () => getMasterEquity(masterId) })
  const today = new Date().toISOString().slice(0, 10)

  if (equity.isError) return <Empty>{describeError(equity.error)}</Empty>
  if (equity.isPending) return <Empty>Загрузка…</Empty>

  const curve = equity.data
  const points = curve.points
  const backfilled = points.filter((point) => point.source === 'backfill').length
  const lastSnapshot = [...points].reverse().find((point) => point.source === 'snapshot') ?? null
  const recent: MasterEquityPoint[] = [...points].reverse().slice(0, 30)

  return (
    <div className={ui.stack}>
      <p className={ui.hint}>
        Честная кривая с первой сделки мастера: виртуальный капитал 10 000 USDT + результат закрытых сделок (прибыль − комиссии + фандинг) + открытые
        позиции по цене фида. Точку дня снимают только часы сервера: каждые {curve.snapshotIntervalMinutes} минут от 00:00 UTC и в {curve.dayCloseUtc} UTC —
        это закрытие дня; ни кнопка, ни перезапуск момент закрытия не выбирают. Дни до первого снимка восстановлены по закрытым сделкам (только
        реализованный результат); пропущенный после этого день остаётся пропуском. Шарп = средняя дневная доходность ÷ её стандартное отклонение ×
        √365, безрисковая ставка 0, по завершённым дням за последние {curve.sharpeWindowDays} дней. Доходность дня не считается, если соседний день
        посчитан иначе (по сделкам ↔ по рынку с открытыми позициями) — такой скачок был бы сменой способа учёта, а не результатом. Дни без позиций
        входят в расчёт нулём, но порог — не меньше {curve.sharpeMinDays} дней в рынке. Витринного значения у Шарпа нет — карточка всегда показывает
        этот факт.
      </p>
      <dl className={ui.kv}>
        <dt>Коэффициент Шарпа</dt>
        <dd className="num">
          {curve.sharpe !== null
            ? signedRatio(curve.sharpe)
            : curve.sharpeTradingDays < curve.sharpeMinDays
              ? `— (мало истории: ${curve.sharpeTradingDays} из ${curve.sharpeMinDays} дн. в рынке)`
              : '— (кривая не менялась)'}
        </dd>
        <dt>Дней в расчёте</dt>
        <dd className="num">
          {curve.sharpeDays} из {curve.sharpeWindowDays} возможных, из них в рынке — {curve.sharpeTradingDays}
        </dd>
        <dt>Точек на кривой</dt>
        <dd className="num">
          {points.length}, из них восстановлено по сделкам — {backfilled}
        </dd>
        <dt>Последний снимок</dt>
        <dd>{lastSnapshot === null ? 'ещё не было' : `${dayLabel(lastSnapshot.day)}, ${utcTime(lastSnapshot.asOf)}`}</dd>
        <dt>Цена сейчас</dt>
        <dd>
          {curve.unpriced.length === 0 ? (
            'свежая по всем открытым позициям'
          ) : (
            <span className={ui.error}>
              нет свежей цены: {curve.unpriced.join(', ')} — точку дня не снять, пока цена не появится
            </span>
          )}
        </dd>
      </dl>
      {recent.length === 0 ? (
        <Empty>Точек пока нет: кривая начнётся с первой сделки мастера</Empty>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>День (UTC)</th>
              <th className={tableClass.right}>Эквити, USDT</th>
              <th className={tableClass.right} title="Результат закрытых сделок: прибыль − комиссии + фандинг">
                Реализовано, USDT
              </th>
              <th className={tableClass.right} title="Открытые позиции по цене фида на момент снимка">
                Открытые по рынку, USDT
              </th>
              <th className={tableClass.right}>Сделок закрыто / открыто</th>
              <th className={tableClass.right} title="Доходность дня, которая идёт в Шарп">
                За день
              </th>
              <th>Точка</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((point) => (
              <tr key={point.day}>
                <td>
                  {dayLabel(point.day)}
                  {point.day === today ? (
                    <div className={tableClass.muted}>сегодня, предварительно</div>
                  ) : point.tradingDay ? null : (
                    <div className={tableClass.muted} title="В этот день у мастера не было открытых позиций: в расчёт идёт нулём, в порог не засчитывается">
                      без позиций
                    </div>
                  )}
                </td>
                <td className={`${tableClass.right} num`}>{money(point.equity, 2)}</td>
                <td className={`${tableClass.right} num`}>{money(point.realized, 2)}</td>
                <td className={`${tableClass.right} num`}>{point.unrealized === null ? '—' : money(point.unrealized, 2)}</td>
                <td className={`${tableClass.right} num`}>
                  {point.closedPositions} / {point.openPositions ?? '—'}
                </td>
                <td className={`${tableClass.right} num`}>
                  {point.dailyReturn === null ? (
                    <span className={tableClass.muted} title="Не входит в расчёт: нет предыдущего дня, или он посчитан иначе (по сделкам ↔ по рынку)">
                      —
                    </span>
                  ) : (
                    signedPct((Number(point.dailyReturn) * 100).toFixed(2))
                  )}
                </td>
                <td>
                  {point.source === 'snapshot' ? (
                    <span title="Снято часами сервера по цене фида">снимок {utcTime(point.asOf)}</span>
                  ) : (
                    <span className={tableClass.muted} title="Восстановлено по закрытым сделкам: только реализованный результат">
                      по сделкам
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}
